import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isMobile } from '../../../utils/commonUtils';
import '../../../styles/LtsContentView.css';
import LtsIntermediateStationContentView from './LtsIntermediateStationContentView';
import {
  formatDateForLtsDatePicker,
  getColorForStationBeingPassed,
  getFontStyleForActual,
  getStationCrossedDot
} from '../utils/ltsCardDetailsUtils';
import { getDateObjectFromDetails } from '../utils/commonLtsUtils';
import { LiveTrainStatusContext } from '../LiveTrainStatus';
import { trackClickEventLTS } from '../../../utils/omnitureUtils';
import { IN_BETWEEN_STATION_CLICKED } from '../constants/LtsTrackingConstants';
import { PROP_64 } from '../../../utils/omnitureUtilConstants';

export function LtsContentViewListItem({ haltingStationIndex }) {
  const { ltsPageState } = useContext(LiveTrainStatusContext);
  const { haltingStationCollection, currentStation, trainStartedFromSource } = { ...ltsPageState };
  const [showIntermediateCollection, setShowIntermediateCollection] = useState(false);
  const [showDateOfStationArrival, setShowDateOfStationArrival] = useState(false);
  const [nextHaltingStationDate, setNextHaltingStationDate] = useState(null);

  const isMobileDevice = isMobile();
  const haltingStation = haltingStationCollection[haltingStationIndex];
  const {
    stationName,
    stationCode,
    platformNumber,
    stoppingMinutes,
    distanceFromOriginStation,
    actualDepartureDetails: { actualDepartureTime, actualDepartureDate } = {},
    scheduledDepartureDetails: { scheduledDepartureTime, scheduledDepartureDate } = {},
    actualArrivalDetails: { actualArrivalTime, actualArrivalDate } = {},
    scheduledArrivalDetails: { scheduledArrivalTime, scheduledArrivalDate } = {},
    intermediateStations
  } = haltingStation;

  useEffect(() => {
    const haltingStationDate = formatDateForLtsDatePicker(
      getDateObjectFromDetails({
        dateInString: actualArrivalDate || scheduledArrivalDate,
        timeInString: '00:00:00'
      })
    );
    if (haltingStationIndex !== haltingStationCollection.length - 1) {
      const {
        actualArrivalDetails: { actualArrivalDate: actualArrivalDateOfNextHalting } = {},
        scheduledArrivalDetails: { scheduledArrivalDate: scheduledArrivalDateOfNextHalting } = {}
      } = haltingStationCollection[haltingStationIndex + 1];
      const haltingNextStationDate = formatDateForLtsDatePicker(
        getDateObjectFromDetails({
          dateInString: actualArrivalDateOfNextHalting || scheduledArrivalDateOfNextHalting,
          timeInString: '00:00:00'
        })
      );
      setShowDateOfStationArrival(haltingStationDate !== haltingNextStationDate);
      setNextHaltingStationDate(haltingNextStationDate);
    }
  }, [ltsPageState]);
  const colorForStationBeingPassedAndBelowIt = getColorForStationBeingPassed({
    haltingStation,
    haltingStationCollection,
    currentStation,
    trainStartedFromSource
  });
  const anchorTagShown = showDateOfStationArrival && !showIntermediateCollection;
  const fontStyleForActualArrival = getFontStyleForActual({
    scheduledDate: scheduledArrivalDate,
    scheduledTime: scheduledArrivalTime,
    actualDate: actualArrivalDate,
    actualTime: actualArrivalTime
  });
  const fontStyleForActualDeparture = getFontStyleForActual({
    scheduledDate: scheduledDepartureDate,
    scheduledTime: scheduledDepartureTime,
    actualDate: actualDepartureDate,
    actualTime: actualDepartureTime
  });

  const isHaltingStationSameAsCurrentStation = haltingStation === currentStation;
  return (
    <li key={haltingStationIndex}>
      <section className="appendBottom2">
        {anchorTagShown && showDateOfStationArrival && nextHaltingStationDate && (
          <span className={`dateOfStationArrival`}>{nextHaltingStationDate}</span>
        )}
        <span
          className={`${getStationCrossedDot({
            haltingStation,
            haltingStationCollection,
            currentStation,
            trainStartedFromSource
          })} stationDot`}
        />
        <div className="makeFlex">
          <div className="makeFlex column stationDistance">
            <span className={`latoBlack stationCodeText`}>{stationCode}</span>
            <span className="darkGreyText alignSelf--center">{distanceFromOriginStation} kms</span>
          </div>
          <span
            className={`coloredStationUpperSegment ${colorForStationBeingPassedAndBelowIt} ${
              haltingStationIndex === 0 ? 'firstStation' : ''
            }`}
          />
          {isHaltingStationSameAsCurrentStation && (
            <span className={`coloredStationUpperSegment belowLottieInUpperSegment`} />
          )}
          <div className="stationName">
            <span className={`latoBlack stationNameText textOverflowEllipsis150`}>{stationName}</span>
            <span className="darkGreyText">
              Platform {platformNumber === '0' || !platformNumber ? '--' : `#${platformNumber}`}
            </span>
            <div></div>
          </div>
          {!isMobileDevice && (
            <span className="platFormWidth darkGreyText">
              Platform {platformNumber === '0' || !platformNumber ? '--' : `#${platformNumber}`}
            </span>
          )}
          {!isMobileDevice && (
            <div className={'appendRight24'}>
              <span className="appendRight12 darkGreyText">
                Stop <span className={'latoBold blackText'}>{stoppingMinutes || '0'}m</span>
              </span>
              <span className="railSeoSprite stopWatch" />
            </div>
          )}
          <div className="pushRight">
            <div className={`${isMobileDevice ? 'makeFlex' : ''}`}>
              <span className={` latoBold ${!isMobileDevice ? 'appendRight24' : 'appendRight8'}`}>ARR.</span>
              <span className={`latoBlack ${!isMobileDevice ? 'appendRight24' : 'appendRight8'}`}>
                {scheduledArrivalTime}
              </span>
              <span className={fontStyleForActualArrival}>{actualArrivalTime}</span>
            </div>
            <div className={`${isMobileDevice ? 'makeFlex' : ''}`}>
              <span className={` latoBold ${!isMobileDevice ? 'appendRight24' : 'appendRight8'} appendLeft3`}>
                DEP.
              </span>
              <span className={`latoBlack ${!isMobileDevice ? 'appendRight24' : 'appendRight8'}`}>
                {scheduledDepartureTime}
              </span>
              <span className={fontStyleForActualDeparture}>{actualDepartureTime}</span>
            </div>
            {isMobileDevice && (
              <div>
                <span className="appendRight12 darkGreyText">
                  Stop <span className={'latoBold blackText'}>{stoppingMinutes || '0'}m</span>
                </span>
                <span className="railSeoSprite stopWatch" />
              </div>
            )}
          </div>
        </div>
      </section>
      {isHaltingStationSameAsCurrentStation && trainStartedFromSource && <div className="ltsTrain" id="lottieForLts" />}
      {intermediateStations && intermediateStations.length ? (
        <a
          href="#0;"
          className="stationExpand"
          onClick={() => {
            if (!showIntermediateCollection) trackClickEventLTS(IN_BETWEEN_STATION_CLICKED, PROP_64);
            setShowIntermediateCollection(!showIntermediateCollection);
          }}
        >
          <span>
            {showIntermediateCollection ? 'Tap to hide stations' : `${intermediateStations.length} In-between stations`}
          </span>
          <span className={'showStraightLine'} />
          <span className={`arrow--blue appendLeft90 ${showIntermediateCollection ? 'up' : 'down'}`} />
        </a>
      ) : null}
      <LtsIntermediateStationContentView
        haltingStation={haltingStation}
        intermediateStations={intermediateStations}
        parentShowIntermediateCollection={showIntermediateCollection}
        updateParentShowIntermediateCollection={setShowIntermediateCollection}
      />
      <span
        className={`coloredStationLowerSegment
        ${haltingStation !== currentStation ? colorForStationBeingPassedAndBelowIt : 'noLiveDetailsKnown'}`}
      />
    </li>
  );
}

LtsContentViewListItem.propTypes = {
  haltingStationIndex: PropTypes.number
};

export default LtsContentViewListItem;
